import { type ReactNode, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useIntl } from 'react-intl';
import { clsx } from 'clsx';
import hooverNoIcon from '../../../assets/icons/account-termination/hoover-no.svg';
import styles from './FilterableList.scss';

interface IterableItem {
  uuid: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- expected usage
interface FilterableListProps<OptionType extends IterableItem, PassthroughProps extends Record<string, any>> {
  options: OptionType[];
  filterValue: string;
  filterFunction: (options: OptionType[], filter: string) => OptionType[];
  itemComponent: (props: PassthroughProps & { item: OptionType; filterValue: string }) => ReactNode | null;
  passThroughProps?: PassthroughProps;
  notFoundMessage?: string;
  className?: string;
}

const DEBOUNCE_DELAY_MILLS = 400;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- expected usage
const FilterableList = <OptionType extends IterableItem, PassthroughProps extends Record<string, any>>({
  options,
  filterValue,
  filterFunction,
  itemComponent: ItemComponent,
  passThroughProps,
  notFoundMessage,
  className,
}: FilterableListProps<OptionType, PassthroughProps>) => {
  const intl = useIntl();

  const [filteredOptions, setFilteredOptions] = useState(options);
  const [debouncedFilter, setDebouncedFilter] = useState(filterValue);

  const filterList = (filter: string) => {
    if (filter) {
      setFilteredOptions(filterFunction(options, filter));
    } else {
      setFilteredOptions(options);
    }
    setDebouncedFilter(filter);
  };

  const onFilterChange = useDebouncedCallback(filterList, DEBOUNCE_DELAY_MILLS);

  useEffect(() => {
    onFilterChange(filterValue);
  }, [filterValue]);

  useEffect(() => {
    filterList(filterValue);
  }, [options, filterFunction]);

  return (
    <div className={clsx(styles.container, className)}>
      {filteredOptions.length > 0 ? (
        filteredOptions.map((item) => (
          <ItemComponent
            key={item.uuid}
            {...(passThroughProps as PassthroughProps)}
            item={item}
            filterValue={debouncedFilter}
          />
        ))
      ) : (
        <div className={styles.noResults}>
          <img src={hooverNoIcon} width="58" height="44" alt="" />
          {notFoundMessage ??
            intl.formatMessage({
              id: 'searchField.noResults',
              defaultMessage: 'Not found',
            })}
        </div>
      )}
    </div>
  );
};

export default FilterableList;
